import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import './LoginPage.scss';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError(t('resetPassword.errors.missingToken'));
      return;
    }
    if (password.length < 6) {
      setError(t('resetPassword.errors.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(t('resetPassword.success'));
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || t('resetPassword.errors.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__container">
        <button type="button" className="page-back" onClick={() => navigate('/login')} aria-label={t('nav.back')}>
          ← {t('nav.back')}
        </button>
        <header className="login-page__header">
          <h1 className="login-page__title">{t('resetPassword.title')}</h1>
        </header>
        <p>{t('resetPassword.subtitle')}</p>
        <form className="login-page__form" onSubmit={handleSubmit}>
          {error && <div className="form-error-message">{error}</div>}
          {success && <div className="form-success-message">{success}</div>}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('resetPassword.form.password')}
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t('resetPassword.form.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('resetPassword.form.loading') : t('resetPassword.form.submit')}
          </button>
        </form>
        <p className="login-page__footer">
          <Link to="/login" className="link">
            {t('resetPassword.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
